import { render } from 'preact';
import { effect, untracked } from '@preact/signals';
import { App } from '@/App.js';
import styles from '@/styles/index.css?inline';
import { configState, resetConfig } from '@/state/config.js';
import { resetSession } from '@/state/session.js';
import { resetCustomerForm } from '@/state/customer-form.js';
import { resetRateInputs } from '@/state/rate-inputs.js';
import { resetStep } from '@/flows/marketplace/state/step.js';
import { resetPaymentForm } from '@/flows/outreach/state/payment-form.js';

const ELEMENT_NAME = 'sell-more';
/** Prod URL is the last fallback so a misconfigured build never points a dealer at dev. */
const DEFAULT_API_BASE =
  (import.meta.env.VITE_WIDGET_API_BASE_DEFAULT as string | undefined) ??
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  'https://api.sellmoreprotection.com';

class SellMoreElement extends HTMLElement {
  /* `closed`, in a real `#private` field and not a TypeScript `private` one that
     is readable at runtime, so `element.shadowRoot` is `null` and a dealer's
     analytics or session recorder cannot read the customer's PII out of the form.
     Not a security boundary — a page that patches `attachShadow` first still
     wins — but it closes every accidental path. */
  #shadow: ShadowRoot;

  /** Disposes the config announcement below, so a remount doesn't stack subscriptions. */
  #stopConfigAnnounce: (() => void) | null = null;

  constructor() {
    super();
    this.#shadow = this.attachShadow({ mode: 'closed' });
  }

  connectedCallback() {
    const apiKey = this.getAttribute('api-key');
    const apiBase = this.getAttribute('api-base') ?? DEFAULT_API_BASE;
    // Absent on a dealer's embed and on our hosted marketplace page; set by our hosted offer
    // page. Both fall back to the dealer-embed behaviour; `App` documents what each changes.
    const outreachId = this.getAttribute('outreach-id');
    const dismissible = this.getAttribute('dismissible') !== 'false';

    if (!apiKey) {
      console.error('[sell-more] missing required attribute: api-key');
      return;
    }

    // Fresh state per mount. All of this is module-level, so without an explicit
    // reset a remount (the demo's "Load widget", or the element being moved in the
    // DOM) resumes the previous session mid-flow.
    resetConfig();
    resetSession();
    resetCustomerForm(); // after resetSession: seeds from the now-cleared `customer`
    resetRateInputs();
    resetStep();
    resetPaymentForm();

    // Mark the host in dev so the `[data-dev]` rule in global.css lowers the fullscreen
    // overlay, letting the demo page's floating controls sit above it. Prod skips this.
    if (__SM_DEV__) {
      this.setAttribute('data-dev', '');
    }

    const style = document.createElement('style');
    style.textContent = styles;
    this.#shadow.appendChild(style);

    const mount = document.createElement('div');
    this.#shadow.appendChild(mount);
    render(
      <App apiKey={apiKey} apiBase={apiBase} outreachId={outreachId} dismissible={dismissible} />,
      mount,
    );

    /* The one thing that crosses the closed shadow root, and only outwards: once config
       settles, tell the host page whose account this is — or why we can't say. Failure is
       announced too, so a listener can tell "still loading" from "this key is dead" instead
       of waiting on a silence. The preview harness labels its settings panel from this, and
       picks between offering an install snippet and a link to the hosted page; a dealer's own
       page learns nothing it didn't already know. */
    this.#stopConfigAnnounce = effect(() => {
      const state = configState.value;
      if (state.kind === 'loading') return;
      const detail =
        state.kind === 'ready'
          ? {
              status: 'ready',
              accountName: state.config.accountName,
              hosted: state.hosting.hosted,
              hostedUrl: state.hosting.url,
            }
          : { status: 'error', errorKind: state.errorKind };
      // Untracked so a listener that reads signals doesn't join this effect's dependencies.
      untracked(() => {
        this.dispatchEvent(new CustomEvent('sell-more:config', { detail }));
      });
    });
  }

  disconnectedCallback() {
    this.#stopConfigAnnounce?.();
    this.#stopConfigAnnounce = null;
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, SellMoreElement);
}
